/* @fflow */

import React, { useReducer, useState } from 'react';
// $FlowIgnore
import _ from 'lodash';
// $FlowIgnore
import { connect } from 'react-redux';
import { fetchSidenav } from 'ducks/sidenav';
import { showOptimisticPendingTransaction } from 'ducks/uploadTransactions';

import getFileExtension from 'utils/getFileExtension';
import { api } from 'utils/ajax';
import Upload from 'components/Upload';
import withRouting from 'utils/withRouting';

import {
  UPLOADING,
  NOT_VALIDATED,
  PENDING,
  VALID,
  SUBMITTED,
  INVALID,
  ERROR
} from '../../constants/UploadTypes';

const fileUploadReducer = (state, action) => {
  const file = action?.file;

  switch(action.type) {
    case NOT_VALIDATED:
      return {
        ...state,
         [file.name]: {
          ...state[file.name],
          loaded: 100,
          status: NOT_VALIDATED,
          result: action.result
        }
      }
    case UPLOADING:
      return {
        ...state,
        [file.name]: {
          id: file.name,
          size: file.size,
          type: file.type,
          loaded: file.loaded,
          lastModified: file.lastModified,
          status: UPLOADING,
        }
      }
    case "UPDATE_PERCENT":
       return {
        ...state,
          [file.name]: {
            ...state[file.name],
            loaded: action.percentLoaded
          }
      }
    case "RESET_FILES":
      return {
        ...reduce(state, (acc, f) => ({
        ...acc,
        [f.id]: {
          ...f,
          status: NOT_VALIDATED
        }}))
      }
    case "REMOVE_FILE":
      return _.omit(state, action.id);
    case SUBMITTED:
      return {
        ...state,
        ..._.reduce(action.files, (acc, f) => ({
          ...acc,
          [f.id]: {
            ...f,
            status: SUBMITTED,
            report: _.find(action.subtransactions, t => t.name === f.id)
          }}), {})
      }
    case INVALID:
      return {
        ...state,
        ..._.reduce(action.files, (acc, f) => ({
          ...acc,
          [f.id]: {
            ...f,
            status: INVALID,
            report: _.find(action.subtransactions, t => t.name === f.id)
          }}), {})
      }
    case ERROR:
      return {
        ...state,
        ..._.reduce(action.files, (acc, f) => ({
          ...acc,
          [f.id]: {
            ...f,
            status: ERROR,
            report: action.error
          }
        }), {})
      }
    default:
      return state;
  }
}

let ALLOWED_FILE_TYPES = [ `tsv`, `json`, ];

const UploadContainer = ({ router, closeModal, dispatch, defs }) => {
  const [files, filesDispatch] = useReducer(fileUploadReducer, {});
  const [step, setStep] = useState(0);
  const [txId, setTxId] = useState(``);
  const [errorMessage, setErrorMessage] = useState(``);
  const [improperFiles, setImproperFiles] = useState([]);
  const [viewingImproperFiles, setViewingImproperFiles] = useState(false);

  const onprogress = (evt: any, file: any) => {
    // evt is an ProgressEvent.
    if (evt.lengthComputable) {
      let percentLoaded = Math.round((evt.loaded / evt.total) * 100);
      // Increase the progress bar length.
      if (percentLoaded < 100) {
        filesDispatch({ type: "UPDATE_PERCENT", file, percentLoaded });
      }
    }
  };

  const onload = (evt: any, file: any) => {
    // evt is an ProgressEvent.
    filesDispatch({ type: "NOT_VALIDATED", file, result: evt.target.result })
  };

  const onloadstart = (evt, file) => {
    filesDispatch({ type: "UPLOADING", file})
  }

  const getSideNavData = () => { // update counts in sidenav
    const { program, project } = router.params;
    let projectId = `${program}-${project}`;
    dispatch(fetchSidenav({ projectId, defs }));
  };

  const readFile = (f: any) => {
    let reader = new FileReader();
    reader.onprogress = evt => onprogress(evt, f);
    reader.onload = evt => onload(evt, f);
    reader.onloadstart = evt => onloadstart(evt, f);
    reader.readAsText(f);
  };

  const addFiles = (files: any) => {
    let [ allowedFiles, improperFilesTmp ] =
      /*
       *  Empty files or files with an improper extension
       *  will get split into a separate array to show users
       *  that they were removed.
       */
      _.partition(files, f => {
        return ALLOWED_FILE_TYPES.indexOf(f.name.toLowerCase().split(`.`).pop()) > -1 && f.size > 0;
      });

    let improperFiles = improperFilesTmp.map(f => ({
      name: f.name,
      reason:
        ALLOWED_FILE_TYPES.indexOf(f.name.toLowerCase().split(`.`).pop()) === -1
          ? `Incorrect file type`
          : f.size === 0
            ? `File is empty`
            : ``
    }));

    filesDispatch({ type: "RESET_FILES "})

    allowedFiles.forEach((file) => readFile(file))

    setStep(1)
    setImproperFiles(improperFiles);
  };

  const clearForbidden = () => {
    setImproperFiles([]);
    setViewingImproperFiles(false);
  };

  const toggleImproperFilesView = (viewingImproperFiles: boolean) => {
    setViewingImproperFiles(viewingImproperFiles);
  };

  const deleteFiles = (id: string) => {
    filesDispatch({ type: "REMOVE_FILE", id })
    setStep(Object.keys(files).length - 1 && step)
  };

  const handleErr = (files: any, error: any) => {
    setErrorMessage(error.message);
    filesDispatch({ type: "ERROR", files, error });
  };

  const handleSubmitSuccess = (files: any, response: any) => {
    setTxId(response.transaction_id);
    filesDispatch({ type: "SUBMITTED", files, subtransactions: response.subtransactions })
  };

  const handleSubmitFail = (files: any, response: any) => {
    filesDispatch({ type: INVALID, files, subtransactions: response.subtransactions })
  };

  const validateAll = (): void => {
    closeModal();
    dispatch(showOptimisticPendingTransaction());
    let notValidatedFiles = _.filter(files, f => f.status === NOT_VALIDATED);

    api({
      url: `${router.params.program}/${router.params.project}/bulk/_dry_run?async=true`,
      method: 'put',
      body: createMultiBody(notValidatedFiles)
    });
  };

  const createMultiBody = (files: any) => {
    let body = _.reduce(files, (acc, file) => (
      [ ...acc, {
        name: file.id,
        doc_format: _.capitalize(getFileExtension(file.id)),
        doc: file.result
      }]
    ), []);
    return JSON.stringify(body);
  }

  const submitAll = async (): void => {
    let validFiles = _.filter(files, f => f.status === VALID);
    try {
      let response = await api({
        url: `${router.params.program}/${router.params.project}/bulk`,
        method: 'put',
        body: createMultiBody(validFiles)
      });
      let json = await response.json();
      handleSubmitSuccess(validFiles, json);
    } catch (err) {
      if (err.status === 400) {
        handleSubmitFail(validFiles, err);
      } else {
        handleErr(validFiles, err);
      }
    }
  };

  const isActiveStep = (s: number): boolean => {
    return s <= step;
  };

  const filesList = Object.values(files || {});
  let hasInvalid = filesList.filter(f => f.status === INVALID).length > 0;
  let hasNotValidated = filesList.filter(f => f.status === NOT_VALIDATED).length > 0;
  let allValid = filesList.length > 0 && filesList.filter(f => f.status === VALID).length === filesList.length;
  let allSubmitted = filesList.length > 0 && filesList.filter(f => f.status === SUBMITTED).length === filesList.length;
  let allPending = filesList.length > 0 && filesList.filter(f => f.status === PENDING).length === filesList.length;

  return (
    <Upload
      isActiveStep={ isActiveStep }
      files={ filesList }
      ALLOWED_FILE_TYPES={ ALLOWED_FILE_TYPES }
      viewingImproperFiles={ viewingImproperFiles }
      toggleImproperFilesView={ toggleImproperFilesView }
      improperFiles={ improperFiles }
      clearForbidden={ clearForbidden }
      step={ step }
      delete={ deleteFiles }
      addFiles={ addFiles }
      validateAll={ validateAll }
      submitAll={ submitAll }
      allPending={ allPending }
      allValid={ allValid }
      allSubmitted={ allSubmitted }
      hasInvalid={ hasInvalid }
      hasNotValidated={ hasNotValidated }
      txId={ txId }
      setStep={ setStep }
      errorMessage={ errorMessage }
      closeModal={ closeModal }
      getSideNavData={ getSideNavData }
    />
  );
}

export default connect(
  state => ({
    defs: state.entityDefinitions.defs,
  })
)(withRouting(UploadContainer));
